import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Truck,
  ShieldCheck,
  RotateCcw,
  Headphones,
  ArrowRight,
  Star,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { categories, benefits, testimonials, heroSlides, brands } from "../mock";
import { fetchProducts } from "../lib/api";
import ProductCard from "../components/ProductCard";
import { Button } from "../components/ui/button";

const iconMap = { Truck, ShieldCheck, RotateCcw, Headphones };

function Hero() {
  const [current, setCurrent] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setCurrent((c) => (c + 1) % heroSlides.length), 6000);
    return () => clearInterval(t);
  }, []);

  return (
    <section className="relative h-[70vh] min-h-[460px] overflow-hidden bg-secondary">
      {heroSlides.map((slide, i) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            i === current ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
        >
          <img src={slide.image} alt={slide.title} className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-black/20 to-transparent" />
          <div className="absolute inset-0 flex items-center">
            <div className="max-w-7xl mx-auto px-6 md:px-10 w-full">
              <div className="max-w-lg text-white fade-up">
                <span className="inline-block text-xs tracking-[0.25em] uppercase mb-4 border-b border-white/40 pb-1">
                  Beauty-Tech Premium
                </span>
                <h1 className="font-serif text-4xl md:text-6xl leading-[1.05] mb-4">
                  {slide.title}
                </h1>
                <p className="text-base md:text-lg text-white/85 mb-8">
                  {slide.subtitle}
                </p>
                <Link to={slide.link}>
                  <Button className="rounded-full h-12 px-8 text-base bg-white text-foreground hover:bg-white/90">
                    {slide.cta}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      ))}

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2 z-10">
        {heroSlides.map((_, i) => (
          <button
            key={i}
            onClick={() => setCurrent(i)}
            className={`h-1.5 rounded-full transition-all ${
              i === current ? "w-8 bg-white" : "w-2 bg-white/50"
            }`}
          />
        ))}
      </div>
    </section>
  );
}

export default function Home() {
  const [products, setProducts] = useState([]);

  useEffect(() => {
    fetchProducts().then(setProducts).catch(() => setProducts([]));
  }, []);

  const bestsellers = products.filter((p) => p.badge === "BESTSELLER");
  const newArrivals = products.filter((p) => p.isNew);
  const onSale = products.filter((p) => p.oldPrice).slice(0, 4);

  return (
    <div>
      <Hero />

      {/* Benefits bar */}
      <section className="border-b border-border">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 divide-x divide-border">
          {benefits.map((b) => {
            const Icon = iconMap[b.icon];
            return (
              <div key={b.title} className="flex items-center gap-3 py-5 px-3 md:px-6">
                <Icon className="h-6 w-6 text-primary shrink-0" />
                <div>
                  <p className="text-sm font-medium leading-tight">{b.title}</p>
                  <p className="text-xs text-muted-foreground">{b.desc}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Categories */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="text-center mb-10">
          <h2 className="font-serif text-3xl md:text-4xl mb-2">Compra por categoria</h2>
          <p className="text-muted-foreground">Encontra o dispositivo perfeito para o teu ritual</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {categories.map((c) => (
            <Link
              key={c.id}
              to={`/loja/${c.slug}`}
              className="group relative aspect-[3/4] rounded-lg overflow-hidden"
            >
              <img
                src={c.image}
                alt={c.name}
                className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
              <div className="absolute bottom-0 left-0 p-5 text-white">
                <p className="text-xs text-white/70 uppercase tracking-wider">{c.tagline}</p>
                <h3 className="font-serif text-2xl">{c.name}</h3>
                <span className="inline-flex items-center gap-1 text-sm mt-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  Explorar <ArrowRight className="h-3.5 w-3.5" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Bestsellers */}
      <section className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-end justify-between mb-8">
          <div>
            <span className="text-primary text-sm font-semibold tracking-wide uppercase">Mais vendidos</span>
            <h2 className="font-serif text-3xl md:text-4xl mt-1">Favoritos da comunidade</h2>
          </div>
          <Link to="/loja" className="hidden md:inline-flex items-center gap-1 text-sm font-medium hover:text-primary">
            Ver tudo <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {bestsellers.map((p) => (
            <ProductCard key={p.id} product={p} />
          ))}
        </div>
      </section>

      {/* Promo banner */}
      <section className="max-w-7xl mx-auto px-6 py-12">
        <div className="relative rounded-2xl overflow-hidden bg-[hsl(20,14%,12%)] text-white">
          <img
            src="https://images.unsplash.com/photo-1712482937664-5697b56ed6f1?w=1400&q=80"
            alt="Saldos RADIX"
            className="absolute inset-0 h-full w-full object-cover opacity-40"
          />
          <div className="relative px-8 md:px-16 py-16 md:py-24 max-w-lg">
            <span className="text-primary font-semibold tracking-wide uppercase text-sm">Saldos de verão</span>
            <h2 className="font-serif text-3xl md:text-5xl mt-3 mb-4">Até -70% em dispositivos selecionados</h2>
            <p className="text-white/80 mb-8">Renova o teu ritual de beleza com os melhores gadgets ao melhor preço. Stock limitado.</p>
            <Link to="/loja">
              <Button className="rounded-full h-12 px-8 bg-primary hover:bg-[hsl(12,62%,52%)] text-primary-foreground">
                Aproveitar saldos
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* On sale */}
      <section className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-end justify-between mb-8">
          <div>
            <span className="text-primary text-sm font-semibold tracking-wide uppercase">Ofertas</span>
            <h2 className="font-serif text-3xl md:text-4xl mt-1">Descontos imperdíveis</h2>
          </div>
          <Link to="/loja" className="hidden md:inline-flex items-center gap-1 text-sm font-medium hover:text-primary">
            Ver tudo <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {onSale.map((p) => (
            <ProductCard key={p.id} product={p} />
          ))}
        </div>
      </section>

      {/* New arrivals */}
      {newArrivals.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 py-8">
          <div className="flex items-end justify-between mb-8">
            <div>
              <span className="text-primary text-sm font-semibold tracking-wide uppercase">Novidades</span>
              <h2 className="font-serif text-3xl md:text-4xl mt-1">Acabadas de chegar</h2>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {newArrivals.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        </section>
      )}

      {/* Testimonials */}
      <section className="bg-secondary/50 mt-12">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <div className="text-center mb-10">
            <h2 className="font-serif text-3xl md:text-4xl mb-2">O que dizem as nossas clientes</h2>
            <div className="flex items-center justify-center gap-1 mt-3">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="h-5 w-5 fill-primary text-primary" />
              ))}
              <span className="ml-2 text-sm text-muted-foreground">4.8/5 · +8.000 avaliações</span>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((t) => (
              <div key={t.id} className="bg-card rounded-xl p-6 border border-border">
                <div className="flex gap-0.5 mb-3">
                  {[...Array(t.rating)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 fill-primary text-primary" />
                  ))}
                </div>
                <p className="text-sm leading-relaxed mb-4">"{t.text}"</p>
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold">
                    {t.name.charAt(0)}
                  </div>
                  <div>
                    <p className="text-sm font-medium">{t.name}</p>
                    <p className="text-xs text-muted-foreground">{t.location}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Brands strip */}
      <section className="max-w-7xl mx-auto px-6 py-12">
        <p className="text-center text-xs uppercase tracking-widest text-muted-foreground mb-6">
          As nossas marcas parceiras
        </p>
        <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-4">
          {brands.map((b) => (
            <span key={b} className="font-serif text-xl md:text-2xl text-muted-foreground/70">
              {b}
            </span>
          ))}
        </div>
      </section>
    </div>
  );
}
